import { useNavigate } from 'react-router-dom';

const services = [
  {
    title: 'Addiction Treatment Programs',
    description: 'Evidence-based Methadone & Suboxone treatment, harm reduction support, and take-home Naloxone kits — all under one roof.',
    image: 'https://readdy.ai/api/search-image?query=warm%20inviting%20addiction%20counseling%20therapy%20room%20with%20comfortable%20chairs%20plants%20on%20windowsill%20soft%20natural%20light%20streaming%20in%20modern%20wellness%20clinic%20interior%20calm%20peaceful%20atmosphere%20muted%20warm%20tones%20two%20people%20in%20session%20professional%20setting&width=600&height=680&seq=addiction-therapy-scene-v1&orientation=portrait',
    link: '/addiction-services',
    external: false
  },
  {
    title: 'Pharmacy Services',
    description: 'Full-service community pharmacy offering prescription dispensing, minor ailment prescribing, and personalized medication management.',
    image: 'https://readdy.ai/api/search-image?query=modern%20pharmacy%20interior%20with%20pharmacist%20helping%20customer%20at%20counter%20warm%20lighting%20professional%20healthcare%20setting%20shelves%20with%20medication%20clean%20organized%20space%20friendly%20service%20interaction%20medical%20clinic%20pharmacy&width=600&height=680&seq=pharmacy-scene-v1&orientation=portrait',
    link: '/pharmacy-services',
    external: false
  },
  {
    title: 'Toronto Safe Clinics & Pharmacies',
    description: 'Connecting you to a trusted network of clinics and pharmacies across Toronto dedicated to safer, stigma-free healthcare.',
    image: 'https://readdy.ai/api/search-image?query=supportive%20healthcare%20team%20meeting%20in%20modern%20clinic%20harm%20reduction%20program%20group%20session%20warm%20supportive%20atmosphere%20professional%20wellness%20facility%20diverse%20group%20of%20people%20natural%20warm%20lighting%20community%20health%20center&width=600&height=680&seq=tscp-clinic-scene-v1&orientation=portrait',
    link: 'https://tscp.ca',
    external: true
  }
];

export default function OurServicesCards() {
  const navigate = useNavigate();

  const handleCardClick = (service: typeof services[0]) => {
    if (service.external) {
      window.open(service.link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(service.link);
    }
  };

  return (
    <section
      className="py-16 md:py-24 w-full overflow-hidden"
      style={{ backgroundColor: '#f4f1ec' }}
      aria-labelledby="services-heading"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 w-full">

        {/* Section Header */}
        <header className="mb-4">
          <p
            className="text-xs font-semibold tracking-widest uppercase mb-4"
            style={{ color: '#8a8070', letterSpacing: '0.18em' }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle"
              style={{ backgroundColor: '#8a8070' }}
              aria-hidden="true"
            />
            Services
          </p>
        </header>

        <div className="flex items-start justify-between gap-8 mb-12 md:mb-16">
          <h2
            id="services-heading"
            className="text-3xl sm:text-4xl md:text-5xl leading-tight max-w-lg font-bold"
            style={{ color: '#2c2820', letterSpacing: '-0.01em' }}
          >
            Comprehensive addiction &amp; pharmacy care in Toronto
          </h2>
          <nav aria-label="Services navigation" className="flex-shrink-0 pt-2">
            <a
              href="/addiction-services"
              onClick={(e) => { e.preventDefault(); navigate('/addiction-services'); }}
              className="inline-block px-6 py-3 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 hover:opacity-90 cursor-pointer"
              style={{ backgroundColor: '#a39b88', color: '#fff', textDecoration: 'none' }}
            >
              View All Services
            </a>
          </nav>
        </div>

        {/* 3-Column Photo Cards */}
        <ul
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 list-none p-0 m-0"
          role="list"
        >
          {services.map((service, index) => (
            <li key={index}>
              <article
                onClick={() => handleCardClick(service)}
                className="group relative rounded-2xl overflow-hidden cursor-pointer w-full"
                style={{ height: '420px' }}
                aria-label={`${service.title} — click to learn more`}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleCardClick(service)}
              >
                {/* Background Photo */}
                <img
                  src={service.image}
                  alt={`${service.title} at Thornhill Drug-Mart`}
                  title={service.title}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  width={600}
                  height={680}
                />

                {/* Dark overlay */}
                <div
                  className="absolute inset-0"
                  aria-hidden="true"
                  style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.08) 30%, rgba(0,0,0,0.62) 70%, rgba(0,0,0,0.78) 100%)' }}
                />

                {/* Card Header — Service Title */}
                <header className="absolute top-5 left-5 right-5 z-10">
                  <h3
                    className="text-white text-base font-semibold leading-snug"
                    style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
                  >
                    {service.title}
                  </h3>
                </header>

                {/* Description + CTA Footer */}
                <footer className="absolute bottom-5 left-5 right-5 z-10">
                  <p
                    className="text-white text-sm leading-relaxed mb-4 opacity-90"
                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
                  >
                    {service.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span
                      className="text-white text-sm font-medium"
                      style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
                    >
                      Read More
                    </span>
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-transform duration-200 group-hover:translate-x-1"
                      style={{ backgroundColor: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)' }}
                      aria-hidden="true"
                    >
                      <i className="ri-arrow-right-line text-sm" />
                    </span>
                  </div>
                </footer>
              </article>
            </li>
          ))}
        </ul>

      </div>
    </section>
  );
}
